import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { BoardExportToNoteDialog } from "./BoardExportToNoteDialog";
import { BoardInCanvasControls } from "./BoardInCanvasControls";
import { BoardReaction } from "./BoardReaction";
import { BoardRemoteCursors } from "./BoardRemoteCursors";
import { BoardSticky } from "./BoardSticky";
import { BoardStroke } from "./BoardStroke";
import { BoardToolbar } from "./BoardToolbar";
import {
  BOARD_EMOJIS,
  BOARD_TOOLS,
  DEFAULT_STICKY,
  DRAW_COLORS,
  ERASER_RADIUS,
  MAX_BOARD_UNDO,
  findStrokeIdsAt,
  newBoardId,
  newStickyId,
  nextZIndex,
} from "../../lib/boardConstants";
import { boardExportFilename, computeBoardContentBounds, downloadBoardPng, renderBoardToPng } from "../../lib/boardExport";
import { cn } from "../../lib/utils";

const CUSTOM_BOARD_EMOJI_KEY = "taistu_board_custom_emojis";

function loadCustomEmojis() {
  try {
    const raw = localStorage.getItem(CUSTOM_BOARD_EMOJI_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((e) => typeof e === "string" && e.trim()) : [];
  } catch {
    return [];
  }
}

const CANVAS_SIZE = 6000;
const MIN_ZOOM = 0.35;
const MAX_ZOOM = 2.5;
const CONTENT_VIEW_PAD = 72;

function viewportIntersectsBounds(pan, zoom, viewW, viewH, bounds) {
  if (!bounds || viewW <= 0 || viewH <= 0) return true;
  const pad = CONTENT_VIEW_PAD;
  const minX = bounds.minX - pad;
  const minY = bounds.minY - pad;
  const maxX = bounds.maxX + pad;
  const maxY = bounds.maxY + pad;
  const vLeft = -pan.x / zoom;
  const vTop = -pan.y / zoom;
  const vRight = vLeft + viewW / zoom;
  const vBottom = vTop + viewH / zoom;
  return !(vRight < minX || vLeft > maxX || vBottom < minY || vTop > maxY);
}

function panZoomToFitContent(bounds, viewW, viewH) {
  const pad = 48;
  const contentW = Math.max(bounds.maxX - bounds.minX + pad * 2, 120);
  const contentH = Math.max(bounds.maxY - bounds.minY + pad * 2, 120);
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;
  const nextZoom = clamp(Math.min(viewW / contentW, viewH / contentH, 1.25), MIN_ZOOM, MAX_ZOOM);
  return {
    zoom: nextZoom,
    pan: {
      x: viewW / 2 - centerX * nextZoom,
      y: viewH / 2 - centerY * nextZoom,
    },
  };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function cloneElements(list) {
  return JSON.parse(JSON.stringify(list || []));
}

export function BoardCanvas({
  elements,
  setElements,
  remoteCursors,
  sendCursor,
  connected,
  loading,
  selfUserId,
  embedded = false,
  expanded = false,
  clientId,
  clientName,
  isAdmin = false,
  onNoteUpdated,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const elementsSnapshotRef = useRef(elements);
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const erasedStrokeIdsRef = useRef(new Set());
  const eraserHistoryPushedRef = useRef(false);
  const [pan, setPan] = useState({ x: 80, y: 80 });
  const [zoom, setZoom] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [tool, setTool] = useState(BOARD_TOOLS.select);
  const [drawColor, setDrawColor] = useState(DRAW_COLORS[0]);
  const [drawWidth, setDrawWidth] = useState(4);
  const [selectedEmoji, setSelectedEmoji] = useState(BOARD_EMOJIS[0]);
  const [customEmojis, setCustomEmojis] = useState(loadCustomEmojis);
  const [activeStroke, setActiveStroke] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const panDragRef = useRef(null);

  const contentBounds = useMemo(() => computeBoardContentBounds(elements), [elements]);

  const isFarFromContent = useMemo(() => {
    if (!contentBounds || loading) return false;
    return !viewportIntersectsBounds(
      pan,
      zoom,
      containerSize.width,
      containerSize.height,
      contentBounds,
    );
  }, [contentBounds, containerSize.height, containerSize.width, loading, pan, zoom]);

  const moveBackToContent = useCallback(() => {
    if (!contentBounds || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const next = panZoomToFitContent(contentBounds, rect.width, rect.height);
    setPan(next.pan);
    setZoom(next.zoom);
  }, [contentBounds]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  const stickies = useMemo(
    () => elements.filter((el) => !el.type || el.type === "sticky"),
    [elements],
  );
  const strokes = useMemo(
    () => elements.filter((el) => el.type === "stroke"),
    [elements],
  );
  const reactions = useMemo(
    () => elements.filter((el) => el.type === "reaction"),
    [elements],
  );

  const emojiOptions = useMemo(() => {
    const extra = customEmojis.filter((emoji) => !BOARD_EMOJIS.includes(emoji));
    return [...BOARD_EMOJIS, ...extra];
  }, [customEmojis]);

  const addCustomEmoji = useCallback((emoji) => {
    const nextEmoji = [...emoji].slice(0, 1).join("");
    if (!nextEmoji || BOARD_EMOJIS.includes(nextEmoji)) return;
    setCustomEmojis((prev) => {
      if (prev.includes(nextEmoji)) return prev;
      const next = [...prev, nextEmoji];
      localStorage.setItem(CUSTOM_BOARD_EMOJI_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => {
    elementsSnapshotRef.current = elements;
  }, [elements]);

  const syncHistoryState = useCallback(() => {
    setCanUndo(undoRef.current.length > 0);
    setCanRedo(redoRef.current.length > 0);
  }, []);

  const pushHistory = useCallback(() => {
    undoRef.current = [
      ...undoRef.current.slice(-(MAX_BOARD_UNDO - 1)),
      cloneElements(elementsSnapshotRef.current),
    ];
    redoRef.current = [];
    syncHistoryState();
  }, [syncHistoryState]);

  const undo = useCallback(() => {
    if (!undoRef.current.length) return;
    redoRef.current = [
      ...redoRef.current.slice(-(MAX_BOARD_UNDO - 1)),
      cloneElements(elementsSnapshotRef.current),
    ];
    const previous = undoRef.current.pop();
    syncHistoryState();
    setElements(previous);
    setSelectedId(null);
    setActiveStroke(null);
  }, [setElements, syncHistoryState]);

  const redo = useCallback(() => {
    if (!redoRef.current.length) return;
    undoRef.current = [
      ...undoRef.current.slice(-(MAX_BOARD_UNDO - 1)),
      cloneElements(elementsSnapshotRef.current),
    ];
    const next = redoRef.current.pop();
    syncHistoryState();
    setElements(next);
    setSelectedId(null);
    setActiveStroke(null);
  }, [setElements, syncHistoryState]);

  const screenToWorld = useCallback(
    (clientX, clientY) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return {
        x: (clientX - rect.left - pan.x) / zoom,
        y: (clientY - rect.top - pan.y) / zoom,
      };
    },
    [pan.x, pan.y, zoom],
  );

  const updateElement = useCallback(
    (id, patch) => {
      setElements((prev) =>
        prev.map((el) => (el.id === id ? { ...el, ...patch } : el)),
      );
    },
    [setElements],
  );

  const deleteElement = useCallback(
    (id) => {
      pushHistory();
      setElements((prev) => prev.filter((el) => el.id !== id));
      if (selectedId === id) setSelectedId(null);
    },
    [pushHistory, selectedId, setElements],
  );

  const eraseAtPoint = useCallback(
    (world) => {
      const radius = ERASER_RADIUS / zoom;
      let removed = false;
      setElements((prev) => {
        const strokeEls = prev.filter((el) => el.type === "stroke");
        const toRemove = findStrokeIdsAt(strokeEls, world.x, world.y, radius).filter(
          (id) => !erasedStrokeIdsRef.current.has(id),
        );
        if (!toRemove.length) return prev;
        removed = true;
        toRemove.forEach((id) => erasedStrokeIdsRef.current.add(id));
        return prev.filter((el) => !toRemove.includes(el.id));
      });
      if (removed && !eraserHistoryPushedRef.current) {
        pushHistory();
        eraserHistoryPushedRef.current = true;
      }
    },
    [pushHistory, setElements, zoom],
  );

  const addSticky = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    const centerX = rect ? (rect.width / 2 - pan.x) / zoom : 200;
    const centerY = rect ? (rect.height / 2 - pan.y) / zoom : 200;
    const sticky = {
      id: newStickyId(),
      type: "sticky",
      x: centerX - DEFAULT_STICKY.width / 2,
      y: centerY - DEFAULT_STICKY.height / 2,
      width: DEFAULT_STICKY.width,
      height: DEFAULT_STICKY.height,
      text: "",
      color: DEFAULT_STICKY.color,
      zIndex: nextZIndex(elements),
    };
    pushHistory();
    setElements((prev) => [...prev, sticky]);
    setSelectedId(sticky.id);
    setTool(BOARD_TOOLS.select);
  }, [elements, pan.x, pan.y, pushHistory, setElements, zoom]);

  const commitStroke = useCallback(
    (stroke) => {
      if (!stroke?.points?.length || stroke.points.length < 2) {
        setActiveStroke(null);
        return;
      }
      pushHistory();
      setElements((prev) => [...prev, stroke]);
      setActiveStroke(null);
    },
    [pushHistory, setElements],
  );

  const onCanvasPointerDown = useCallback(
    (event) => {
      if (event.button !== 0) return;

      if (tool === BOARD_TOOLS.draw) {
        event.preventDefault();
        const world = screenToWorld(event.clientX, event.clientY);
        const stroke = {
          id: newBoardId("stroke"),
          type: "stroke",
          points: [[world.x, world.y]],
          strokeColor: drawColor,
          strokeWidth: drawWidth,
          zIndex: nextZIndex(elements),
          x: 0,
          y: 0,
        };
        setActiveStroke(stroke);
        setSelectedId(null);
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }

      if (tool === BOARD_TOOLS.eraser) {
        event.preventDefault();
        erasedStrokeIdsRef.current = new Set();
        eraserHistoryPushedRef.current = false;
        eraseAtPoint(screenToWorld(event.clientX, event.clientY));
        setSelectedId(null);
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }

      if (tool === BOARD_TOOLS.emoji && selectedEmoji) {
        const world = screenToWorld(event.clientX, event.clientY);
        const reaction = {
          id: newBoardId("reaction"),
          type: "reaction",
          emoji: selectedEmoji,
          x: world.x,
          y: world.y,
          size: 48,
          zIndex: nextZIndex(elements),
        };
        pushHistory();
        setElements((prev) => [...prev, reaction]);
        setSelectedId(reaction.id);
        return;
      }

      setSelectedId(null);
      panDragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        originX: pan.x,
        originY: pan.y,
      };

      const onMove = (moveEvent) => {
        if (!panDragRef.current) return;
        setPan({
          x: panDragRef.current.originX + (moveEvent.clientX - panDragRef.current.startX),
          y: panDragRef.current.originY + (moveEvent.clientY - panDragRef.current.startY),
        });
      };

      const onUp = () => {
        panDragRef.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [
      drawColor,
      drawWidth,
      elements,
      eraseAtPoint,
      pan.x,
      pan.y,
      pushHistory,
      screenToWorld,
      selectedEmoji,
      setElements,
      tool,
    ],
  );

  const onCanvasPointerMove = useCallback(
    (event) => {
      if (activeStroke && tool === BOARD_TOOLS.draw) {
        if (event.cancelable) event.preventDefault();
        const world = screenToWorld(event.clientX, event.clientY);
        setActiveStroke((prev) => {
          if (!prev) return prev;
          const last = prev.points[prev.points.length - 1];
          const minDist = event.pointerType === "touch" ? 0.75 / zoom : 2 / zoom;
          if (last && Math.hypot(last[0] - world.x, last[1] - world.y) < minDist) {
            return prev;
          }
          return { ...prev, points: [...prev.points, [world.x, world.y]] };
        });
        return;
      }

      if (tool === BOARD_TOOLS.eraser && event.buttons === 1) {
        eraseAtPoint(screenToWorld(event.clientX, event.clientY));
      }
    },
    [activeStroke, eraseAtPoint, screenToWorld, tool, zoom],
  );

  const onCanvasPointerUp = useCallback(
    (event) => {
      if (activeStroke && tool === BOARD_TOOLS.draw) {
        commitStroke(activeStroke);
      }
      if (tool === BOARD_TOOLS.eraser) {
        erasedStrokeIdsRef.current = new Set();
        eraserHistoryPushedRef.current = false;
      }
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    [activeStroke, commitStroke, tool],
  );

  const onWheel = useCallback(
    (event) => {
      event.preventDefault();
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const delta = event.deltaY > 0 ? -0.08 : 0.08;
      const nextZoom = clamp(zoom + delta, MIN_ZOOM, MAX_ZOOM);
      const mx = event.clientX - rect.left;
      const my = event.clientY - rect.top;
      const scale = nextZoom / zoom;
      setPan((p) => ({
        x: mx - (mx - p.x) * scale,
        y: my - (my - p.y) * scale,
      }));
      setZoom(nextZoom);
    },
    [zoom],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;
      const tag = event.target?.tagName?.toLowerCase();
      if (tag === "textarea" || tag === "input") return;

      const key = event.key.toLowerCase();
      if (key === "z" && event.shiftKey) {
        event.preventDefault();
        redo();
        return;
      }
      if (key === "y" && !event.shiftKey) {
        event.preventDefault();
        redo();
        return;
      }
      if (key === "z" && !event.shiftKey) {
        event.preventDefault();
        undo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [redo, undo]);

  const onPointerMove = useCallback(
    (event) => {
      const world = screenToWorld(event.clientX, event.clientY);
      sendCursor(world.x, world.y);
    },
    [screenToWorld, sendCursor],
  );

  const strokesInteractive = tool !== BOARD_TOOLS.eraser && tool !== BOARD_TOOLS.draw;

  const canvasCursor =
    tool === BOARD_TOOLS.draw
      ? "crosshair"
      : tool === BOARD_TOOLS.eraser
        ? "cell"
        : tool === BOARD_TOOLS.emoji
          ? "copy"
          : undefined;

  const handleDownloadBoard = useCallback(async () => {
    try {
      const { blob } = await renderBoardToPng(elements);
      downloadBoardPng(blob, boardExportFilename(clientName));
      toast.success("Board downloaded");
    } catch (error) {
      toast.error(error.message || "Could not export board");
    }
  }, [clientName, elements]);

  return (
    <div className={cn("flex min-h-0 flex-col", embedded ? "min-w-0" : "")} data-testid="board-canvas">
      <BoardToolbar
        connected={connected}
        onAddSticky={addSticky}
        tool={tool}
        onToolChange={setTool}
        drawColor={drawColor}
        onDrawColorChange={setDrawColor}
        drawWidth={drawWidth}
        onDrawWidthChange={setDrawWidth}
        selectedEmoji={selectedEmoji}
        onSelectEmoji={setSelectedEmoji}
        emojiOptions={emojiOptions}
        onAddCustomEmoji={addCustomEmoji}
      />
      <div
        ref={containerRef}
        className={cn(
          "relative touch-none overflow-hidden rounded-[var(--t-event-pill-radius)] border border-[var(--t-border)] bg-[#FAFAFA]",
          expanded
            ? embedded
              ? "h-[min(calc(100vh-260px),700px)] min-h-[440px]"
              : "h-[min(calc(100vh-300px),840px)] min-h-[520px]"
            : embedded
              ? "h-[min(62vh,560px)]"
              : "h-[min(72vh,680px)]",
        )}
        style={canvasCursor ? { cursor: canvasCursor } : undefined}
        onPointerMove={onPointerMove}
      >
        {!loading ? (
          <BoardInCanvasControls
            onUndo={undo}
            onRedo={redo}
            canUndo={canUndo}
            canRedo={canRedo}
            onDownloadBoard={handleDownloadBoard}
            onAddBoardToNote={() => setExportDialogOpen(true)}
            isAdmin={isAdmin}
          />
        ) : null}
        {!loading && isFarFromContent ? (
          <button
            type="button"
            onClick={moveBackToContent}
            className="absolute bottom-3 left-1/2 z-20 -translate-x-1/2 rounded-full bg-white/80 px-3 py-1.5 text-xs text-[var(--t-meta)] backdrop-blur-sm transition-colors hover:text-[var(--t-accent)]"
            data-testid="board-move-back-to-content"
          >
            Move back to content
          </button>
        ) : null}
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-[var(--t-meta)]">
            Loading board…
          </div>
        ) : null}
        <div
          className="absolute left-0 top-0 origin-top-left will-change-transform"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          }}
        >
          <div
            ref={canvasRef}
            className={cn(
              "relative touch-none select-none",
              tool === BOARD_TOOLS.select && "cursor-grab active:cursor-grabbing",
            )}
            style={{
              width: CANVAS_SIZE,
              height: CANVAS_SIZE,
              backgroundImage: "radial-gradient(circle, rgba(0,0,0,0.12) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
            onPointerDown={onCanvasPointerDown}
            onPointerMove={onCanvasPointerMove}
            onPointerUp={onCanvasPointerUp}
            onPointerCancel={onCanvasPointerUp}
          >
            <svg
              className="pointer-events-none absolute left-0 top-0"
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              aria-hidden
            >
              <g className="pointer-events-auto">
                {strokes.map((stroke) => (
                  <BoardStroke
                    key={stroke.id}
                    element={stroke}
                    selected={selectedId === stroke.id}
                    onSelect={setSelectedId}
                    interactive={strokesInteractive}
                  />
                ))}
                {activeStroke ? (
                  <BoardStroke element={activeStroke} selected={false} onSelect={() => {}} interactive={false} />
                ) : null}
              </g>
            </svg>

            {stickies.map((element) => (
              <BoardSticky
                key={element.id}
                element={element}
                selected={selectedId === element.id}
                onSelect={setSelectedId}
                onChange={updateElement}
                onDelete={deleteElement}
                zoom={zoom}
              />
            ))}

            {reactions.map((element) => (
              <BoardReaction
                key={element.id}
                element={element}
                selected={selectedId === element.id}
                onSelect={setSelectedId}
                onChange={updateElement}
                onDelete={deleteElement}
                zoom={zoom}
              />
            ))}

            <BoardRemoteCursors cursors={remoteCursors} selfUserId={selfUserId} />
          </div>
        </div>
      </div>

      <BoardExportToNoteDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        clientId={clientId}
        clientName={clientName}
        elements={elements}
        onSaved={(note) => {
          onNoteUpdated?.(note);
        }}
      />
    </div>
  );
}
